import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Inbox, MapPin, MessageCircle, Package, RefreshCw, Search } from 'lucide-react';
import { fetchAdminTriageSummary, fetchConversations } from './api';
import { socket, socketAuthPayload } from '../../admin/chat/socket';
import { deliveryLocationPresentation, deliveryStatusPresentation } from './deliveryContextUtils';

const LOCATION_BADGE_CLASSES = {
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  amber: 'border-amber-200 bg-amber-50 text-amber-700',
  red: 'border-red-200 bg-red-50 text-red-700',
};

function formatTime(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString('es-PE', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function mergeById(existing, incoming) {
  const seen = new Set();
  return [...existing, ...incoming].filter(item => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function activityTimeMs(item) {
  const value = item?.latest_message?.created_at || item?.updated_at || item?.created_at;
  const timestamp = value ? new Date(value).getTime() : 0;
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function sortByLatestActivity(items) {
  return [...items].sort((a, b) => {
    const activityDiff = activityTimeMs(b) - activityTimeMs(a);
    if (activityDiff !== 0) return activityDiff;
    return Number(b.id || 0) - Number(a.id || 0);
  });
}

function itemMatchesFilter(item, filter, { excludeSupport = false } = {}) {
  if (excludeSupport && filter !== 'support_general' && item.type === 'support_general') return false;
  if (!filter || filter === 'all') return true;
  const triage = item.triage || {};
  if (filter === 'unread') return Boolean(item.unread);
  if (filter === 'pending_draft') return Boolean(triage.has_pending_draft);
  if (filter === 'driver_issue') return (triage.participant_types || []).includes('driver') || Boolean(item.delivery?.driver_name);
  if (filter === 'customer_not_answering') return triage.latest_intent === 'customer_not_answering' || item.latest_intent === 'customer_not_answering';
  if (filter === 'ai_handoff') return Boolean(triage.requires_human);
  if (filter === 'unresolved_identity') return Boolean(triage.has_unresolved_identity || item.type === 'unresolved_whatsapp');
  if (filter === 'cod_payment') return item.delivery?.charge != null || Boolean(triage.has_cod_payment);
  if (filter === 'whatsapp') return Boolean(triage.has_whatsapp_active || item.latest_message?.channel === 'whatsapp');
  if (filter === 'delivery_issue') return Boolean(triage.has_delivery_risk || ['medium', 'high', 'critical'].includes(item.risk_level));
  if (filter === 'support_general') return item.type === 'support_general';
  if (['store', 'provider', 'driver', 'final_customer'].includes(filter)) return (triage.participant_types || []).includes(filter);
  return true;
}

function triageBadgeClass(label) {
  if (label === 'critical') return 'bg-red-100 text-red-700 border-red-200';
  if (label === 'high') return 'bg-orange-100 text-orange-700 border-orange-200';
  if (label === 'medium') return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
}

function supportTargetFromParticipants(item) {
  if (item.support_target) return item.support_target;
  const participant = (item.participants || []).find(p => ['driver', 'store', 'provider'].includes(p.participant_type));
  if (!participant) {
    return {
      name: item.summary || `Soporte #${item.id}`,
      role_label: 'Participante',
      user_id: item.store_user_id,
      driver_id: item.driver_id,
      provider_id: item.provider_id,
      phone_number: null,
    };
  }
  const roleLabels = {
    driver: 'Motorizado',
    store: 'Cliente/Tienda',
    provider: 'Proveedor',
  };
  return {
    name: participant.role_label || item.summary || `Soporte #${item.id}`,
    role: participant.participant_type,
    role_label: roleLabels[participant.participant_type] || 'Participante',
    user_id: participant.user_id,
    driver_id: participant.driver_id,
    provider_id: item.provider_id,
    phone_number: participant.phone_number,
  };
}

function supportTargetId(target) {
  if (!target) return null;
  if (target.role === 'driver') return target.driver_id || target.user_id;
  if (target.role === 'provider') return target.provider_id || target.user_id;
  return target.user_id || target.driver_id || target.provider_id;
}

function latestMessagePreview(message) {
  const text = (message?.content || '').trim();
  if (text) return text;
  const type = (message?.message_type || message?.media_type || '').toLowerCase();
  if (type.includes('image') || message?.media_url?.match(/\.(png|jpe?g|webp|gif)(\?|$)/i)) return 'Imagen';
  if (type.includes('audio') || message?.media_url?.match(/\.(mp3|m4a|ogg|wav|webm)(\?|$)/i)) return 'Audio';
  if (type.includes('location') || type.includes('map') || message?.latitude || message?.longitude) return 'Ubicación';
  if (type.includes('contact')) return 'Contacto';
  return 'Sin mensajes';
}

const DEFAULT_FILTERS = [
  ['all', 'Todos'],
  ['unread', 'Sin leer'],
  ['delivery_issue', 'Incidencias'],
  ['driver', 'Motorizado'],
];

export default function ConversationList({
  scope = 'admin',
  userId,
  selectedConversationId,
  onSelect,
  title = 'Comunicación',
  subtitle = 'Hub interno + WhatsApp',
  limit = 40,
  filterOptions = DEFAULT_FILTERS,
  extraParams = null,
  searchPlaceholder = 'Buscar entrega, tienda, cliente o mensaje',
  showCommandCenter = false,
  enableRealtime = false,
}) {
  const [items, setItems] = useState([]);
  const [triageSummary, setTriageSummary] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(null);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const triageSummaryTimerRef = useRef(null);
  const extraParamsKey = JSON.stringify(extraParams || {});
  const commandCards = [
    ['ai_handoff', 'Human', triageSummary?.needs_human || 0, 'border-amber-200 bg-amber-50 text-amber-800'],
    ['pending_draft', 'Drafts', triageSummary?.pending_draft || 0, 'border-purple-200 bg-purple-50 text-purple-800'],
    ['unresolved_identity', 'Identity', triageSummary?.unresolved_identity || 0, 'border-red-200 bg-red-50 text-red-800'],
    ['customer_not_answering', 'No contesta', triageSummary?.customer_not_answering || 0, 'border-red-200 bg-red-50 text-red-800'],
    ['delivery_issue', 'Risk', triageSummary?.delivery_risk || 0, 'border-orange-200 bg-orange-50 text-orange-800'],
    ['whatsapp', 'WhatsApp', triageSummary?.whatsapp_active || 0, 'border-emerald-200 bg-emerald-50 text-emerald-800'],
    ['support_general', 'Fast Max', triageSummary?.support_general || 0, 'border-pink-200 bg-pink-50 text-pink-800'],
  ];
  const primaryFilterIds = new Set(
    showCommandCenter
      ? ['all', 'unread', 'ai_handoff', 'pending_draft', 'customer_not_answering', 'whatsapp', 'delivery_issue', 'support_general']
      : filterOptions.map(([id]) => id)
  );
  const primaryFilters = filterOptions.filter(([id]) => primaryFilterIds.has(id));
  const moreFilters = showCommandCenter
    ? filterOptions.filter(([id]) => !primaryFilterIds.has(id))
    : [];
  const activeMoreFilter = moreFilters.find(([id]) => id === filter);

  const selectFilter = useCallback((id) => {
    setFilter(id);
    setShowMoreFilters(false);
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const load = useCallback(async ({ reset = false, offset = 0 } = {}) => {
    if (!userId && scope !== 'admin') return;
    reset ? setIsLoading(true) : setIsLoadingMore(true);
    try {
      const params = {
        ...(extraParams || {}),
        limit,
        offset,
        q: search || undefined,
        filter: filter === 'all' ? undefined : filter,
        user_id: scope === 'admin' ? userId : undefined,
      };
      const data = await fetchConversations(scope, scope === 'admin' ? params : userId, params);
      const nextItems = data.items || [];
      setItems(prev => (reset ? sortByLatestActivity(nextItems) : sortByLatestActivity(mergeById(prev, nextItems))));
      if (data.triage_summary && filter === 'all') setTriageSummary(data.triage_summary);
      setHasMore(Boolean(data.has_more));
      setNextOffset(data.next_offset ?? null);
    } catch (error) {
      console.error('Conversation list error:', error);
      if (reset) setItems([]);
      setHasMore(false);
      setNextOffset(null);
    } finally {
      reset ? setIsLoading(false) : setIsLoadingMore(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraParamsKey, filter, limit, scope, search, userId]);

  const loadTriageSummary = useCallback(async () => {
    if (scope !== 'admin' || !showCommandCenter) return;
    try {
      const summaryExtraParams = JSON.parse(extraParamsKey || '{}');
      const data = await fetchAdminTriageSummary({
        ...summaryExtraParams,
        q: search || undefined,
        user_id: userId || undefined,
      });
      setTriageSummary(data);
    } catch (error) {
      console.error('Triage summary error:', error);
    }
  }, [extraParamsKey, scope, search, showCommandCenter, userId]);

  const refresh = useCallback(() => {
    load({ reset: true, offset: 0 });
  }, [load]);

  const scheduleRefresh = useCallback((delay = 500) => {
    if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = window.setTimeout(() => {
      refreshTimerRef.current = null;
      refresh();
    }, delay);
  }, [refresh]);

  const scheduleTriageSummary = useCallback((delay = 1200) => {
    if (triageSummaryTimerRef.current) window.clearTimeout(triageSummaryTimerRef.current);
    triageSummaryTimerRef.current = window.setTimeout(() => {
      triageSummaryTimerRef.current = null;
      loadTriageSummary();
    }, delay);
  }, [loadTriageSummary]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading || isLoadingMore || nextOffset == null) return;
    load({ reset: false, offset: nextOffset });
  }, [hasMore, isLoading, isLoadingMore, load, nextOffset]);

  useEffect(() => {
    refresh();
    scheduleTriageSummary(100);
  }, [refresh, scheduleTriageSummary]);

  useEffect(() => () => {
    if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
    if (triageSummaryTimerRef.current) window.clearTimeout(triageSummaryTimerRef.current);
  }, []);

  useEffect(() => {
    if (!showCommandCenter && !enableRealtime && scope === 'admin') return undefined;
    const roomPayload = socketAuthPayload(
      scope === 'admin'
        ? {}
        : {
            scope,
            user_id: userId,
          }
    );
    const joinRoom = () => {
      if (scope === 'admin') {
        socket.emit('join_communications_admin', roomPayload);
      } else if (userId) {
        socket.emit('join_communications_scope', roomPayload);
      }
    };
    const rejoinAndRefetch = () => {
      joinRoom();
      refresh();
      scheduleTriageSummary(300);
    };
    const leave = () => {
      if (scope === 'admin') {
        socket.emit('leave_communications_admin', roomPayload);
      } else if (userId) {
        socket.emit('leave_communications_scope', roomPayload);
      }
    };
    const upsertConversation = (payload) => {
      const next = payload?.conversation || null;
      if (!next?.id) {
        scheduleRefresh();
        scheduleTriageSummary();
        return;
      }
      if (payload?.triage && !next.triage) next.triage = payload.triage;
      if (search) {
        scheduleRefresh();
      } else {
        setItems(prev => {
          const merged = [next, ...prev.filter(item => item.id !== next.id)];
          return sortByLatestActivity(merged.filter(item => itemMatchesFilter(item, filter, {
            excludeSupport: scope === 'admin',
          })));
        });
      }
      scheduleTriageSummary();
    };
    const handleMessage = (payload) => upsertConversation(payload);
    const handleUpdate = (payload) => upsertConversation(payload);
    const handleTriage = (payload) => upsertConversation(payload);
    const handleAI = (payload) => upsertConversation(payload);
    const handleRead = (payload) => upsertConversation(payload);
    const handleIntelligence = (payload) => upsertConversation(payload);
    const handleAction = (payload) => upsertConversation(payload);

    joinRoom();
    socket.on('connect', rejoinAndRefetch);
    socket.on('conversation_message_created', handleMessage);
    socket.on('conversation_updated', handleUpdate);
    socket.on('conversation_triage_updated', handleTriage);
    socket.on('conversation_ai_state_updated', handleAI);
    socket.on('conversation_read_updated', handleRead);
    socket.on('conversation_intelligence_updated', handleIntelligence);
    socket.on('conversation_action_completed', handleAction);

    return () => {
      socket.off('connect', rejoinAndRefetch);
      socket.off('conversation_message_created', handleMessage);
      socket.off('conversation_updated', handleUpdate);
      socket.off('conversation_triage_updated', handleTriage);
      socket.off('conversation_ai_state_updated', handleAI);
      socket.off('conversation_read_updated', handleRead);
      socket.off('conversation_intelligence_updated', handleIntelligence);
      socket.off('conversation_action_completed', handleAction);
      leave();
    };
  }, [enableRealtime, filter, refresh, scheduleRefresh, scheduleTriageSummary, scope, search, showCommandCenter, userId]);

  useEffect(() => {
    const node = sentinelRef.current;
    const root = scrollRef.current;
    if (!node || !root || !hasMore) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root, rootMargin: '180px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  return (
    <div className="flex h-full flex-col border-r border-slate-200 bg-white">
      <div className="border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-extrabold text-slate-950 font-montserrat">{title}</h2>
            <p className="text-[11px] text-slate-500">{subtitle}</p>
          </div>
          <button
            onClick={refresh}
            className="rounded-lg bg-slate-100 p-2 text-slate-600 hover:bg-slate-200"
            title="Actualizar"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="mt-2 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            placeholder={searchPlaceholder}
            className="w-full bg-transparent text-sm outline-none"
          />
        </div>

        {showCommandCenter && (
          <div className="mt-2 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            {commandCards.map(([id, label, count, klass]) => (
              <button
                key={id}
                onClick={() => selectFilter(id)}
                className={`flex min-w-[96px] items-center gap-2 rounded-xl border px-2.5 py-2 text-left transition hover:shadow-sm ${klass} ${
                  filter === id ? 'ring-2 ring-[#B1336E]/40' : ''
                }`}
              >
                <div className="text-lg font-black leading-none">{count}</div>
                <div className="text-[10px] font-extrabold uppercase leading-tight tracking-wide">{label}</div>
              </button>
            ))}
          </div>
        )}

        <div className="relative mt-2">
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
          {primaryFilters.map(([id, label]) => (
            <button
              key={id}
              onClick={() => selectFilter(id)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold ${
                filter === id ? 'bg-[#B1336E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {label}
            </button>
          ))}
          {moreFilters.length > 0 && (
            <button
              onClick={() => setShowMoreFilters(value => !value)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold ${
                activeMoreFilter ? 'bg-[#B1336E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {activeMoreFilter ? `Más: ${activeMoreFilter[1]}` : 'Más filtros'}
            </button>
          )}
          </div>
          {showMoreFilters && moreFilters.length > 0 && (
            <div className="absolute left-0 right-0 top-full z-20 mt-2 rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
              <div className="grid grid-cols-2 gap-2">
                {moreFilters.map(([id, label]) => (
                  <button
                    key={id}
                    onClick={() => selectFilter(id)}
                    className={`rounded-lg px-3 py-2 text-xs font-bold ${
                      filter === id ? 'bg-[#B1336E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {items.map(item => {
          const isSupport = item.type === 'support_general';
          const isUnresolved = item.type === 'unresolved_whatsapp';
          const supportTarget = isSupport ? supportTargetFromParticipants(item) : null;
          const supportId = supportTargetId(supportTarget);
          const supportTitle = supportTarget?.name || `Soporte #${item.id}`;
          const supportPreview = `Soporte Fast Max · ${supportTarget?.role_label || 'Participante'}${supportId ? ` · ID ${supportId}` : ''}`;
          const latestPreview = latestMessagePreview(item.latest_message);
          const delivery = item.delivery || null;
          const deliveryStatus = deliveryStatusPresentation(delivery?.status);
          const deliveryLocation = deliveryLocationPresentation(delivery || {});
          const deliveryMeta = [
            `Entrega #${item.delivery_point_id || item.id}`,
            delivery?.product || 'Producto sin detalle',
            delivery?.district,
          ].filter(Boolean).join(' · ');
          return (
          <button
            key={item.id}
            onClick={() => onSelect?.(item)}
            className={`w-full border-b border-slate-100 p-4 text-left hover:bg-slate-50 ${
              selectedConversationId === item.id ? 'bg-[#B1336E]/5' : ''
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                {item.unread ? (
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#B1336E]" />
                ) : isSupport || isUnresolved ? (
                  <MessageCircle className="h-4 w-4 shrink-0 text-slate-400" />
                ) : (
                  <Package className="h-4 w-4 shrink-0 text-[#B1336E]" />
                )}
                <span className="truncate text-sm font-bold text-slate-900">
                  {isUnresolved
                    ? `WhatsApp sin identidad #${item.id}`
                    : isSupport
                      ? supportTitle
                      : (delivery?.recipient_name || `Entrega #${item.delivery_point_id || item.id}`)}
                </span>
              </div>
              <span className="shrink-0 text-[11px] text-slate-400">
                {formatTime(item.latest_message?.created_at || item.updated_at)}
              </span>
            </div>
            <p className="mt-1 truncate text-xs font-medium text-slate-500">
              {isSupport
                ? supportPreview
                : isUnresolved
                  ? 'Requiere resolver identidad'
                  : deliveryMeta}
            </p>
            {delivery && !isSupport && !isUnresolved && (
              <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1.5">
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${deliveryStatus.classes}`}>
                  {deliveryStatus.label}
                </span>
                <span className={`inline-flex min-w-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${LOCATION_BADGE_CLASSES[deliveryLocation.tone]}`}>
                  <MapPin className="h-3 w-3 shrink-0" />
                  <span className="truncate">{deliveryLocation.shortLabel}</span>
                </span>
              </div>
            )}
            <p className="mt-1.5 line-clamp-2 text-sm text-slate-700">
              {latestPreview}
            </p>
            {item.triage && (
              <div className="mt-2 flex flex-wrap items-center gap-1">
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-extrabold ${triageBadgeClass(item.triage.priority_label)}`}>
                  {item.triage.priority_label} · {item.triage.priority_score}
                </span>
                {item.triage.requires_human && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">Human</span>
                )}
                {item.triage.has_pending_draft && (
                  <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-bold text-purple-700">Draft</span>
                )}
                {((item.triage.latest_intent === 'customer_not_answering') || item.latest_intent === 'customer_not_answering') && (
                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">Cliente no contesta</span>
                )}
                {item.triage.has_unresolved_identity && (
                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">ID</span>
                )}
                {(item.delivery?.charge != null || item.triage.has_cod_payment) && (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">COD</span>
                )}
                {item.triage.has_whatsapp_active && (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">WA</span>
                )}
              </div>
            )}
          </button>
        );
        })}

        {(isLoading || isLoadingMore) && (
          <div className="flex items-center justify-center gap-2 p-4 text-sm text-slate-500">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Cargando conversaciones...
          </div>
        )}

        {!items.length && !isLoading && (
          <div className="flex flex-col items-center justify-center p-8 text-center text-slate-500">
            <Inbox className="mb-3 h-10 w-10 text-slate-300" />
            <p className="text-sm">No hay conversaciones</p>
          </div>
        )}

        {hasMore && !isLoading && (
          <div ref={sentinelRef} className="p-4 text-center text-xs text-slate-400">
            Cargando más al desplazarte...
          </div>
        )}
      </div>
    </div>
  );
}
